import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface LogsSkeletonProps {
	/** Fixed row count. Omit to scale the count to the measured height. */
	rows?: number
	showSearchBar?: boolean
	showHistogram?: boolean
	className?: string
	label?: string | false
}

const LOG_ROW_HEIGHT = 33
// The search bar, histogram and pagination footer only earn their space on tall panels.
const MIN_HEIGHT_FOR_HISTOGRAM = 320
const MIN_HEIGHT_FOR_FOOTER = 180
const MIN_HEIGHT_FOR_SEARCH_BAR = 220
// Rendered height of each non-row block, subtracted before the rows claim what is left.
const SEARCH_BAR_HEIGHT = 52
const HISTOGRAM_MIN_HEIGHT = 100
const HISTOGRAM_HEIGHT_RATIO = 0.3
// The "Count" label and date axis drawn around the histogram bars.
const HISTOGRAM_LABELS_HEIGHT = 24
const FOOTER_HEIGHT = 56
const TABLE_HEADER_HEIGHT = 40
// The fixed-width columns add up to ~600px — drop them on narrow panels instead of clipping them.
const MIN_WIDTH_FOR_DEVICE_COLUMN = 420
const MIN_WIDTH_FOR_TAG_COLUMNS = 620

const MESSAGE_WIDTHS = ['w-[250px]', 'w-[400px]', 'w-[180px]', 'w-[320px]', 'w-[200px]', 'w-[350px]']
const TAG_WIDTHS = ['w-12', 'w-20', 'w-16', 'w-10', 'w-14']
const LEVEL_WIDTHS = ['w-12', 'w-20', 'w-16', 'w-10', 'w-14']

// A pattern of heights representing a small count histogram spike like in the screenshot
const HISTOGRAM_HEIGHTS = [
	'h-1',
	'h-1.5',
	'h-1',
	'h-2',
	'h-10',
	'h-12',
	'h-6',
	'h-2',
	'h-1',
	'h-1',
	'h-1.5',
	'h-1',
	'h-2',
	'h-1',
	'h-1',
	'h-1.5',
	'h-1',
	'h-2',
	'h-1',
	'h-1',
	'h-1',
	'h-1.5',
	'h-1',
	'h-2',
	'h-1',
]

export function LogsSkeleton({
	rows,
	showSearchBar = false,
	showHistogram = true,
	className,
	label = 'Loading logs',
}: LogsSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()

	// Reserve space for whichever blocks survive at this height, then fill the rest with rows.
	const withSearchBar = showSearchBar && (size.height === 0 || size.height >= MIN_HEIGHT_FOR_SEARCH_BAR)
	const withHistogram = showHistogram && (size.height === 0 || size.height >= MIN_HEIGHT_FOR_HISTOGRAM)
	const withFooter = size.height === 0 || size.height >= MIN_HEIGHT_FOR_FOOTER
	const reservedHeight =
		(withSearchBar ? SEARCH_BAR_HEIGHT : 0) +
		(withHistogram
			? Math.max(HISTOGRAM_MIN_HEIGHT, size.height * HISTOGRAM_HEIGHT_RATIO) + HISTOGRAM_LABELS_HEIGHT
			: 0) +
		(withFooter ? FOOTER_HEIGHT : 0) +
		TABLE_HEADER_HEIGHT
	const rowCount = rows ?? fitCount(size.height - reservedHeight, LOG_ROW_HEIGHT, 1, 60)
	const withDeviceColumn = size.width === 0 || size.width >= MIN_WIDTH_FOR_DEVICE_COLUMN
	const withTagColumns = size.width === 0 || size.width >= MIN_WIDTH_FOR_TAG_COLUMNS

	const rowKeys = useStableKeys(rowCount)
	const histogramKeys = useStableKeys(HISTOGRAM_HEIGHTS.length)

	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('flex flex-col h-full w-full min-h-0 min-w-0 overflow-hidden bg-background', className)}>
			{/* 1. Search Bar Skeleton */}
			{withSearchBar && (
				<div className='px-4 pt-2 pb-2 border-b shrink-0'>
					<Skeleton className='h-9 w-full rounded-md bg-accent/60' />
				</div>
			)}

			{/* 2. Histogram Skeleton */}
			{withHistogram && (
				<div className='h-[30%] min-h-25 w-full mt-2 flex flex-col gap-2 px-4 py-2 shrink-0 border-b border-border/40 pb-4'>
					{/* "Count" label */}
					<Skeleton className='h-3 w-10 shrink-0 bg-accent/50' />

					{/* Graph Area */}
					<div className='flex-1 flex items-end justify-between px-6 pt-4 border-b border-l border-border/50 relative'>
						{histogramKeys.map((key, i) => (
							<Skeleton
								key={key}
								className={cn(
									'w-[2.5%] rounded-t-sm transition-all duration-300 bg-accent/40',
									HISTOGRAM_HEIGHTS[i % HISTOGRAM_HEIGHTS.length],
								)}
							/>
						))}
					</div>

					{/* Timeline dates under graph */}
					<div className='flex justify-between text-[10px] text-muted-foreground mt-1 px-4'>
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
						<Skeleton className='h-2.5 w-10 bg-accent/40' />
					</div>
				</div>
			)}

			{/* 3. Table Skeleton */}
			<div className='min-h-0 flex-1 overflow-auto relative scrollbar-hide'>
				<table className='w-full caption-bottom text-body table-fixed'>
					<thead className='sticky top-0 bg-background z-10 [&_tr]:border-b'>
						<tr className='border-b transition-colors hover:bg-muted/50'>
							{withDeviceColumn && (
								<th className='sticky left-0 bg-background z-20 border-r h-10 px-2 text-left align-middle font-medium text-foreground whitespace-nowrap w-37.5'>
									<Skeleton className='h-4 w-12 bg-accent/60' />
								</th>
							)}
							<th className='h-10 px-2 text-center align-middle font-medium text-foreground whitespace-nowrap bg-background w-50'>
								<Skeleton className='h-4 w-28 mx-auto bg-accent/60' />
							</th>
							{withTagColumns && (
								<>
									<th className='h-10 px-2 text-left align-middle font-medium text-foreground whitespace-nowrap bg-background w-37.5'>
										<Skeleton className='h-4 w-10 bg-accent/60' />
									</th>
									<th className='h-10 px-2 text-left align-middle font-medium text-foreground whitespace-nowrap bg-background w-25'>
										<Skeleton className='h-4 w-12 bg-accent/60' />
									</th>
								</>
							)}
							<th className='h-10 px-2 text-left align-middle font-medium text-foreground bg-background'>
								<Skeleton className='h-4 w-16 bg-accent/60' />
							</th>
						</tr>
					</thead>
					<tbody className='[&_tr:last-child]:border-0'>
						{rowKeys.map((rowKey, r) => (
							<tr key={rowKey} className='border-b transition-colors hover:bg-muted/50'>
								{/* IMEI */}
								{withDeviceColumn && (
									<td className='sticky left-0 bg-background border-r p-2 align-middle whitespace-nowrap text-body w-37.5'>
										<Skeleton className='h-4 w-12 bg-accent/50' />
									</td>
								)}
								{/* Timestamp */}
								<td className='p-2 align-middle text-body whitespace-nowrap w-50'>
									<Skeleton className='h-4 w-36 mx-auto bg-accent/50' />
								</td>
								{withTagColumns && (
									<>
										{/* Tag */}
										<td className='p-2 align-middle whitespace-nowrap text-body w-37.5'>
											<Skeleton className={cn('h-4 bg-accent/50', TAG_WIDTHS[r % TAG_WIDTHS.length])} />
										</td>
										{/* Level */}
										<td className='p-2 align-middle whitespace-nowrap font-semibold text-body w-25'>
											<Skeleton className={cn('h-4 bg-accent/50', LEVEL_WIDTHS[r % LEVEL_WIDTHS.length])} />
										</td>
									</>
								)}
								{/* Message */}
								<td className='p-2 align-middle text-body'>
									<Skeleton className={cn('h-4 bg-accent/50', MESSAGE_WIDTHS[r % MESSAGE_WIDTHS.length])} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* 4. Footer Skeleton */}
			{withFooter && (
				<div className='mt-auto shrink-0 border-t bg-background px-4 py-3 flex items-center justify-between'>
					<div className='flex items-center gap-2'>
						<Skeleton className='h-8 w-8 rounded-md bg-accent/50' /> {/* Prev page */}
						<Skeleton className='h-8 w-16 rounded-md bg-accent/50' /> {/* Page indicator */}
						<Skeleton className='h-8 w-8 rounded-md bg-accent/50' /> {/* Next page */}
						<Skeleton className='h-8 w-16 rounded-md bg-accent/50' /> {/* Page size dropdown */}
					</div>
				</div>
			)}
		</SkeletonRegion>
	)
}
